// src/context/AuthContext.js
// ===================================================
// 🔹 Auth Context (Production Ready with Admin Support)
// ===================================================
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { toast } from "react-toastify";

/* ===================================================
   🔐 ADMIN CONFIG (SOURCE OF TRUTH)
   =================================================== */
const ADMIN_EMAILS = [
  "hariharanmadhav@gmail.com",
  "aravintharumugam.m@gmail.com", // 👈 your admin email
];

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);

  /* ---------------------------------------------------
     Persist auth session
  --------------------------------------------------- */
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch((err) =>
      console.error("Persistence error:", err)
    );
  }, []);

  /* ---------------------------------------------------
     Ensure Firestore profile exists (ADMIN FIXED)
  --------------------------------------------------- */
  const ensureUserProfile = useCallback(async (user) => {
    if (!user) return;

    try {
      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);

      const isAdminUser = ADMIN_EMAILS.includes(user.email);

      if (!snap.exists()) {
        await setDoc(ref, {
          uid: user.uid,
          name: user.displayName || "",
          email: user.email || "",
          phone: user.phoneNumber || "",
          photoURL: user.photoURL || "",
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
          isAdmin: isAdminUser, // ✅ FIX
        });
      } else {
        await setDoc(
          ref,
          {
            lastLoginAt: serverTimestamp(),
            // ⚠️ Do NOT downgrade admin accidentally
            ...(isAdminUser ? { isAdmin: true } : {}),
          },
          { merge: true }
        );
      }
    } catch (err) {
      console.error("Profile init error:", err);
      toast.error("Profile setup failed ❌");
    }
  }, []);

  /* ---------------------------------------------------
     Auth state + realtime profile listener
  --------------------------------------------------- */
  useEffect(() => {
    let unsubProfile = null;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser || null);
      setLoading(false);

      if (currentUser) {
        await ensureUserProfile(currentUser);

        const ref = doc(db, "users", currentUser.uid);
        unsubProfile = onSnapshot(
          ref,
          (snap) => {
            if (snap.exists()) {
              setProfile({ id: snap.id, ...snap.data() });
            } else {
              setProfile(null);
            }
            setProfileLoading(false);
          },
          (err) => {
            console.error("Profile snapshot error:", err);
            toast.error("Failed to load profile ❌");
            setProfile(null);
            setProfileLoading(false);
          }
        );
      } else {
        setProfile(null);
        setProfileLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (unsubProfile) unsubProfile();
    };
  }, [ensureUserProfile]);

  /* ---------------------------------------------------
     Auth actions
  --------------------------------------------------- */
  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success("Welcome back 👋");
    } catch (error) {
      handleAuthError(error, "Login failed ❌");
      throw error;
    }
  };

  const register = async (email, password) => {
    try {
      const cred = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      toast.success("Account created 🎉");
      return cred;
    } catch (error) {
      handleAuthError(error, "Registration failed ❌");
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await ensureUserProfile(result.user);
      toast.success("Logged in with Google 🎉");
    } catch (error) {
      handleAuthError(error, "Google login failed ❌");
      throw error;
    }
  };

  const registerWithGoogle = loginWithGoogle;

  const logout = async () => {
    try {
      await signOut(auth);
      toast.info("Logged out 👋");
    } catch (error) {
      toast.error("Logout failed ❌");
    }
  };

  /* ---------------------------------------------------
     Error helper
  --------------------------------------------------- */
  const handleAuthError = (error, fallback) => {
    let message = fallback;
    switch (error.code) {
      case "auth/email-already-in-use":
        message = "This email is already registered.";
        break;
      case "auth/invalid-email":
        message = "Invalid email address.";
        break;
      case "auth/user-not-found":
        message = "No user found with this email.";
        break;
      case "auth/wrong-password":
        message = "Incorrect password.";
        break;
      case "auth/weak-password":
        message = "Password should be at least 6 characters.";
        break;
      default:
        message = error.message || fallback;
    }
    toast.error(message);
  };

  /* ---------------------------------------------------
     Context value
  --------------------------------------------------- */
  const value = {
    user,
    profile,
    isAdmin: !!profile?.isAdmin, // ✅ reliable
    loading: loading || profileLoading,
    login,
    register,
    loginWithGoogle,
    registerWithGoogle,
    googleLogin: loginWithGoogle,
    googleRegister: registerWithGoogle,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!value.loading && children}
    </AuthContext.Provider>
  );
}
